import { useState } from "react";
import * as XLSX from "xlsx";
import NavbarAdmin from "../components/NavbarAdmin";
import { useLanguage } from "../context/LanguageContext";
import { t } from "../i18n";

const API = process.env.REACT_APP_API_URL || "";

function authHeader() {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function fmtDate(iso) {
  if (!iso) return "-";
  return new Date(iso).toLocaleDateString("th-TH", {
    day: "2-digit", month: "2-digit", year: "numeric",
  });
}

function fmtNum(n) {
  if (n == null) return "-";
  return Number(n).toLocaleString("th-TH");
}

const MONTH_NAMES = [
  "มกราคม","กุมภาพันธ์","มีนาคม","เมษายน","พฤษภาคม","มิถุนายน",
  "กรกฎาคม","สิงหาคม","กันยายน","ตุลาคม","พฤศจิกายน","ธันวาคม",
];

function todayISO() {
  const d = new Date();
  const tz = d.getTimezoneOffset() * 60000;
  return new Date(d - tz).toISOString().slice(0, 10);
}

export default function AdminReport() {
  const { lang } = useLanguage();
  const now = new Date();
  const [mode, setMode]           = useState("month"); // "month" | "range" | "nmonths" | "daily"
  const [selMonth, setSelMonth]   = useState(now.getMonth() + 1);
  const [selYear, setSelYear]     = useState(now.getFullYear());
  const [dateFrom, setDateFrom]   = useState("");
  const [dateTo,   setDateTo]     = useState("");
  const [nMonths,  setNMonths]    = useState(3);
  const [dailyDate, setDailyDate] = useState(todayISO());
  const [guest,    setGuest]      = useState("");
  const [rows,     setRows]       = useState(null);
  const [loading,  setLoading]    = useState(false);
  const [error,    setError]      = useState("");

  const years = Array.from({ length: 6 }, (_, i) => now.getFullYear() - 2 + i);

  const buildQuery = () => {
    const params = new URLSearchParams();
    if (mode === "month") {
      const from = new Date(selYear, selMonth - 1, 1);
      const to   = new Date(selYear, selMonth, 0);
      params.set("dateFrom", from.toISOString().slice(0, 10));
      params.set("dateTo",   to.toISOString().slice(0, 10));
    } else if (mode === "nmonths") {
      const to   = new Date(now.getFullYear(), now.getMonth() + 1, 0); // end of current month
      const from = new Date(now.getFullYear(), now.getMonth() - (nMonths - 1), 1);
      params.set("dateFrom", from.toISOString().slice(0, 10));
      params.set("dateTo",   to.toISOString().slice(0, 10));
    } else {
      if (dateFrom) params.set("dateFrom", dateFrom);
      if (dateTo)   params.set("dateTo",   dateTo);
    }
    if (guest.trim()) params.set("guest", guest.trim());
    return params.toString();
  };

  const handleLoad = async () => {
    if (mode === "daily") return handleLoadDaily();
    setLoading(true);
    setError("");
    setRows(null);
    try {
      const res = await fetch(`${API}/api/report/bookings?${buildQuery()}`, {
        headers: authHeader(),
        credentials: "include",
      });
      if (!res.ok) throw new Error(t(lang, "rptLoadFail"));
      const data = await res.json();
      setRows(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLoadDaily = async () => {
    setLoading(true);
    setError("");
    setRows(null);
    try {
      const params = new URLSearchParams({ date: dailyDate });
      const res = await fetch(`${API}/api/report/daily?${params.toString()}`, {
        headers: authHeader(),
        credentials: "include",
      });
      if (!res.ok) throw new Error(t(lang, "rptLoadFail"));
      const data = await res.json();
      let dailyRows = data.rows || [];
      if (guest.trim()) {
        const q = guest.trim().toLowerCase();
        dailyRows = dailyRows.filter(r =>
          (r.guestName || "").toLowerCase().includes(q) ||
          (r.email || "").toLowerCase().includes(q) ||
          (r.phone || "").toLowerCase().includes(q) ||
          (r.roomNumber || "").toLowerCase().includes(q)
        );
      }
      setRows(dailyRows);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleExportDaily = () => {
    if (!rows || rows.length === 0) return;

    const sheetData = rows.map((r) => {
      const vacant = r.status === "Vacant";
      return {
        "เลขห้อง":        r.roomNumber,
        "สถานะห้อง":      vacant ? t(lang, "rptVacant") : t(lang, "rptOccupied"),
        "ชื่อผู้เข้าพัก": vacant ? "" : r.guestName,
        "อีเมล":          vacant ? "" : r.email,
        "เบอร์โทร":       vacant ? "" : r.phone,
        "เข้าห้อง":       vacant ? "" : fmtDate(r.checkIn),
        "ออกห้อง":        vacant ? "" : fmtDate(r.checkOut),
        "ทั้งหมด (บาท)":  vacant ? "" : (r.totalAmount ?? "-"),
        "จ่ายแล้ว (บาท)": vacant ? "" : (r.paidAmount ?? "-"),
        "เหลือ (บาท)":    vacant ? "" : (r.remaining ?? "-"),
      };
    });

    const ws = XLSX.utils.json_to_sheet(sheetData);
    ws["!cols"] = [
      { wch: 8 }, { wch: 12 }, { wch: 24 }, { wch: 28 }, { wch: 14 },
      { wch: 12 }, { wch: 12 }, { wch: 14 }, { wch: 14 }, { wch: 14 },
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "รายงานรายวัน");
    XLSX.writeFile(wb, `รายงานรายวัน_${dailyDate}.xlsx`);
  };

  const handleExport = () => {
    if (!rows || rows.length === 0) return;
    if (mode === "daily") return handleExportDaily();

    const sheetData = rows.map((b, i) => {
      const nights = b.checkIn && b.checkOut
        ? Math.round((new Date(b.checkOut) - new Date(b.checkIn)) / 86400000)
        : "-";
      const guestName = b.user
        ? `${b.user.firstname || b.firstName || ""} ${b.user.lastname || b.lastName || ""}`.trim() || b.user.email
        : b.guestName || "-";
      return {
        "#":             i + 1,
        "ชื่อผู้เข้าพัก": guestName,
        "อีเมล":         b.user?.email || "-",
        "ห้อง":          b.roomUnit?.roomNumber || "-",
        "ประเภทห้อง":    b.roomType?.name || "-",
        "เช็คอิน":       fmtDate(b.checkIn),
        "เช็คเอาต์":     fmtDate(b.checkOut),
        "จำนวนคืน":      nights,
        "ราคารวม (บาท)": b.totalAmount ?? "-",
        "ชำระแล้ว (บาท)":b.paidAmount  ?? "-",
        "วันที่บันทึก":  fmtDate(b.createdAt),
      };
    });

    const ws = XLSX.utils.json_to_sheet(sheetData);

    // Column widths
    ws["!cols"] = [
      { wch: 5 }, { wch: 24 }, { wch: 28 }, { wch: 8 }, { wch: 16 },
      { wch: 12 }, { wch: 12 }, { wch: 10 }, { wch: 16 }, { wch: 16 }, { wch: 14 },
    ];

    const wb = XLSX.utils.book_new();
    const title = mode === "month"
      ? `รายงาน_${MONTH_NAMES[selMonth - 1]}_${selYear + 543}`
      : mode === "nmonths"
      ? `รายงาน_ย้อนหลัง_${nMonths}_เดือน`
      : `รายงาน_${dateFrom}_ถึง_${dateTo}`;
    XLSX.utils.book_append_sheet(wb, ws, "รายงาน");
    XLSX.writeFile(wb, `${title}.xlsx`);
  };

  const totalRevenue = rows ? rows.reduce((s, b) => s + (b.totalAmount || 0), 0) : 0;
  const occupiedCount = mode === "daily" && rows ? rows.filter(r => r.status !== "Vacant").length : 0;
  const vacantCount   = mode === "daily" && rows ? rows.filter(r => r.status === "Vacant").length : 0;
  const outstanding   = mode === "daily" && rows ? rows.reduce((s, r) => s + (r.remaining > 0 ? r.remaining : 0), 0) : 0;

  return (
    <>
      <NavbarAdmin />
      <div style={{ minHeight: "100vh", background: "#f1f3f6", padding: "clamp(16px, 4vw, 32px) clamp(12px, 3vw, 24px)" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>

          {/* Header */}
          <div style={{ marginBottom: 28 }}>
            <h2 style={{ fontSize: 22, fontWeight: 700, color: "#1e293b", margin: 0 }}>
              {t(lang, "rptTitle")}
            </h2>
            <p style={{ color: "#64748b", marginTop: 4, fontSize: 14 }}>
              {t(lang, "rptSub")}
            </p>
          </div>

          {/* Filter card */}
          <div style={{ background: "#fff", borderRadius: 14, padding: 24, boxShadow: "0 1px 8px rgba(0,0,0,.07)", marginBottom: 24 }}>

            {/* Mode toggle */}
            <div style={{ display: "flex", gap: 10, marginBottom: 20, flexWrap: "wrap" }}>
              {[["month", t(lang,"rptSelectMonth")], ["nmonths", t(lang,"rptNMonths")], ["range", t(lang,"rptDateRange")], ["daily", t(lang,"rptDaily")]].map(([v, label]) => (
                <button key={v} onClick={() => setMode(v)}
                  style={{
                    padding: "8px 20px", borderRadius: 8, border: "none", cursor: "pointer",
                    fontWeight: 600, fontSize: 14,
                    background: mode === v ? "#1e3a8a" : "#f1f5f9",
                    color: mode === v ? "#fff" : "#475569",
                    transition: "all .15s",
                  }}>
                  {label}
                </button>
              ))}
            </div>

            <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "flex-end" }}>

              {/* Month / N-months / Range picker */}
              {mode === "month" ? (
                <>
                  <label style={labelStyle}>
                    {t(lang, "rptMonth")}
                    <select value={selMonth} onChange={e => setSelMonth(+e.target.value)} style={inputStyle}>
                      {MONTH_NAMES.map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
                    </select>
                  </label>
                  <label style={labelStyle}>
                    {t(lang, "rptYear")}
                    <select value={selYear} onChange={e => setSelYear(+e.target.value)} style={inputStyle}>
                      {years.map(y => <option key={y} value={y}>{y + 543}</option>)}
                    </select>
                  </label>
                </>
              ) : mode === "nmonths" ? (
                <label style={labelStyle}>
                  {t(lang, "rptNMonthsBack")}
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <input
                      type="number" min={1} max={36} value={nMonths}
                      onChange={e => setNMonths(Math.max(1, Math.min(36, +e.target.value)))}
                      style={{ ...inputStyle, width: 90, textAlign: "center" }}
                    />
                    <span style={{ fontSize: 13, color: "#64748b" }}>
                      ({(() => {
                        const from = new Date(now.getFullYear(), now.getMonth() - (nMonths - 1), 1);
                        return `${MONTH_NAMES[from.getMonth()]} ${from.getFullYear() + 543} – ${MONTH_NAMES[now.getMonth()]} ${now.getFullYear() + 543}`;
                      })()})
                    </span>
                  </div>
                </label>
              ) : mode === "daily" ? (
                <label style={labelStyle}>
                  {t(lang, "rptDailyDate")}
                  <input type="date" value={dailyDate} onChange={e => setDailyDate(e.target.value)} style={inputStyle} />
                </label>
              ) : (
                <>
                  <label style={labelStyle}>
                    {t(lang, "rptDateFrom")}
                    <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={inputStyle} />
                  </label>
                  <label style={labelStyle}>
                    {t(lang, "rptDateTo")}
                    <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} style={inputStyle} />
                  </label>
                </>
              )}

              {/* Guest filter */}
              <label style={{ ...labelStyle, flex: 1, minWidth: 200 }}>
                {t(lang, "rptSearchGuest")}
                <input
                  type="text" value={guest} placeholder={t(lang, "rptSearchPh")}
                  onChange={e => setGuest(e.target.value)}
                  style={{ ...inputStyle, width: "100%" }}
                />
              </label>

              {/* Load button */}
              <button onClick={handleLoad} disabled={loading}
                style={{
                  padding: "10px 28px", borderRadius: 8, border: "none", cursor: loading ? "not-allowed" : "pointer",
                  background: "#1e3a8a", color: "#fff", fontWeight: 700, fontSize: 15,
                  opacity: loading ? 0.7 : 1, whiteSpace: "nowrap",
                  alignSelf: "flex-end",
                }}>
                {loading ? t(lang, "rptLoading") : t(lang, "rptLoad")}
              </button>
            </div>

            {error && (
              <div style={{ marginTop: 12, color: "#dc2626", fontSize: 14 }}>⚠️ {error}</div>
            )}
          </div>

          {/* Results */}
          {rows !== null && (
            <div style={{ background: "#fff", borderRadius: 14, boxShadow: "0 1px 8px rgba(0,0,0,.07)", overflow: "hidden" }}>

              {/* Summary bar */}
              <div style={{ padding: "16px 24px", borderBottom: "1px solid #f1f5f9", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
                <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
                  {mode === "daily" ? (
                    <>
                      <div>
                        <span style={{ fontSize: 12, color: "#64748b" }}>{t(lang, "rptRoomsTotal")}</span>
                        <div style={{ fontWeight: 700, fontSize: 18, color: "#1e293b" }}>{rows.length}</div>
                      </div>
                      <div>
                        <span style={{ fontSize: 12, color: "#64748b" }}>{t(lang, "rptRoomsOccupied")}</span>
                        <div style={{ fontWeight: 700, fontSize: 18, color: "#1e40af" }}>{occupiedCount}</div>
                      </div>
                      <div>
                        <span style={{ fontSize: 12, color: "#64748b" }}>{t(lang, "rptRoomsVacant")}</span>
                        <div style={{ fontWeight: 700, fontSize: 18, color: "#64748b" }}>{vacantCount}</div>
                      </div>
                      <div>
                        <span style={{ fontSize: 12, color: "#64748b" }}>{t(lang, "rptRevenue")}</span>
                        <div style={{ fontWeight: 700, fontSize: 18, color: "#15803d" }}>฿{fmtNum(totalRevenue)}</div>
                      </div>
                      <div>
                        <span style={{ fontSize: 12, color: "#64748b" }}>{t(lang, "rptOutstanding")}</span>
                        <div style={{ fontWeight: 700, fontSize: 18, color: "#b45309" }}>฿{fmtNum(outstanding)}</div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div>
                        <span style={{ fontSize: 12, color: "#64748b" }}>{t(lang, "rptTotal")}</span>
                        <div style={{ fontWeight: 700, fontSize: 18, color: "#1e293b" }}>{rows.length} {t(lang, "rptItems")}</div>
                      </div>
                      <div>
                        <span style={{ fontSize: 12, color: "#64748b" }}>{t(lang, "rptRevenue")}</span>
                        <div style={{ fontWeight: 700, fontSize: 18, color: "#15803d" }}>฿{fmtNum(totalRevenue)}</div>
                      </div>
                    </>
                  )}
                </div>
                <button onClick={handleExport} disabled={rows.length === 0}
                  style={{
                    padding: "10px 24px", borderRadius: 8, border: "none",
                    background: rows.length > 0 ? "#16a34a" : "#d1d5db",
                    color: "#fff", fontWeight: 700, fontSize: 14, cursor: rows.length > 0 ? "pointer" : "not-allowed",
                    display: "flex", alignItems: "center", gap: 8,
                  }}>
                  {t(lang, "rptDownload")}
                </button>
              </div>

              {rows.length === 0 ? (
                <div style={{ padding: 48, textAlign: "center", color: "#94a3b8", fontSize: 15 }}>
                  {t(lang, "rptNoData")}
                </div>
              ) : mode === "daily" ? (
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                    <thead>
                      <tr style={{ background: "#f8fafc", borderBottom: "2px solid #e2e8f0" }}>
                        {[
                          [t(lang,"rptColRoomNo"), true],
                          [t(lang,"rptColRoomStatus"), true],
                          [t(lang,"rptColGuest"), false],
                          [t(lang,"rptColEmail"), false],
                          [t(lang,"rptColPhone"), false],
                          [t(lang,"rptColCheckIn"), false],
                          [t(lang,"rptColCheckOut"), false],
                          [t(lang,"rptColTotal"), false],
                          [t(lang,"rptColPaid"), false],
                          [t(lang,"rptColRemain"), false],
                        ].map(([h, center]) => (
                          <th key={h} style={{ padding: "10px 14px", textAlign: center ? "center" : "left", color: "#475569", fontWeight: 600, whiteSpace: "nowrap" }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r, i) => {
                        const vacant = r.status === "Vacant";
                        return (
                          <tr key={r.roomNumber + i} style={{ borderBottom: "1px solid #f1f5f9", background: vacant ? "#f8fafc" : (i % 2 === 1 ? "#fafafa" : "#fff") }}>
                            <td style={{ padding: "10px 14px", textAlign: "center" }}>
                              <span style={{ background: "#dbeafe", color: "#1e40af", borderRadius: 6, padding: "2px 8px", fontWeight: 600 }}>
                                {r.roomNumber}
                              </span>
                            </td>
                            <td style={{ padding: "10px 14px", textAlign: "center" }}>
                              <span style={{
                                background: vacant ? "#dcfce7" : "#fee2e2",
                                color: vacant ? "#15803d" : "#b91c1c",
                                borderRadius: 999, padding: "3px 12px", fontWeight: 600, whiteSpace: "nowrap",
                              }}>
                                {vacant ? t(lang, "rptVacant") : t(lang, "rptOccupied")}
                              </span>
                            </td>
                            <td style={{ padding: "10px 14px", fontWeight: 600, color: vacant ? "#94a3b8" : "#1e293b" }}>
                              {vacant ? "" : r.guestName}
                            </td>
                            <td style={{ padding: "10px 14px", color: "#64748b" }}>{vacant ? "" : r.email}</td>
                            <td style={{ padding: "10px 14px", color: "#64748b" }}>{vacant ? "" : r.phone}</td>
                            <td style={{ padding: "10px 14px", color: "#64748b", whiteSpace: "nowrap" }}>{vacant ? "" : fmtDate(r.checkIn)}</td>
                            <td style={{ padding: "10px 14px", color: "#64748b", whiteSpace: "nowrap" }}>{vacant ? "" : fmtDate(r.checkOut)}</td>
                            <td style={{ padding: "10px 14px", color: "#15803d", fontWeight: 600, textAlign: "right" }}>
                              {vacant ? "" : (r.totalAmount != null ? `฿${fmtNum(r.totalAmount)}` : "-")}
                            </td>
                            <td style={{ padding: "10px 14px", color: "#1e293b", fontWeight: 600, textAlign: "right" }}>
                              {vacant ? "" : (r.paidAmount != null ? `฿${fmtNum(r.paidAmount)}` : "-")}
                            </td>
                            <td style={{ padding: "10px 14px", color: r.remaining > 0 ? "#b45309" : "#15803d", fontWeight: 600, textAlign: "right" }}>
                              {vacant ? "" : (r.remaining != null ? `฿${fmtNum(r.remaining)}` : "-")}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                    <thead>
                      <tr style={{ background: "#f8fafc", borderBottom: "2px solid #e2e8f0" }}>
                        {[
                          [t(lang,"rptColNum"), true],
                          [t(lang,"rptColGuest"), false],
                          [t(lang,"rptColEmail"), false],
                          [t(lang,"rptColRoom"), false],
                          [t(lang,"rptColType"), false],
                          [t(lang,"rptColCheckIn"), false],
                          [t(lang,"rptColCheckOut"), false],
                          [t(lang,"rptColNights"), true],
                          [t(lang,"rptColTotal"), false],
                          [t(lang,"rptColPaid"), false],
                        ].map(([h, center]) => (
                          <th key={h} style={{ padding: "10px 14px", textAlign: center ? "center" : "left", color: "#475569", fontWeight: 600, whiteSpace: "nowrap" }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((b, i) => {
                        const nights = b.checkIn && b.checkOut
                          ? Math.round((new Date(b.checkOut) - new Date(b.checkIn)) / 86400000)
                          : "-";
                        const guestName = b.user
                          ? `${b.user.firstname || b.firstName || ""} ${b.user.lastname || b.lastName || ""}`.trim() || b.user.email
                          : b.guestName || "-";
                        return (
                          <tr key={b._id} style={{ borderBottom: "1px solid #f1f5f9", background: i % 2 === 1 ? "#fafafa" : "#fff" }}>
                            <td style={{ padding: "10px 14px", textAlign: "center", color: "#94a3b8" }}>{i + 1}</td>
                            <td style={{ padding: "10px 14px", fontWeight: 600, color: "#1e293b" }}>{guestName}</td>
                            <td style={{ padding: "10px 14px", color: "#64748b" }}>{b.user?.email || "-"}</td>
                            <td style={{ padding: "10px 14px", textAlign: "center" }}>
                              <span style={{ background: "#dbeafe", color: "#1e40af", borderRadius: 6, padding: "2px 8px", fontWeight: 600 }}>
                                {b.roomUnit?.roomNumber || "-"}
                              </span>
                            </td>
                            <td style={{ padding: "10px 14px", color: "#64748b" }}>{b.roomType?.name || "-"}</td>
                            <td style={{ padding: "10px 14px", color: "#64748b", whiteSpace: "nowrap" }}>{fmtDate(b.checkIn)}</td>
                            <td style={{ padding: "10px 14px", color: "#64748b", whiteSpace: "nowrap" }}>{fmtDate(b.checkOut)}</td>
                            <td style={{ padding: "10px 14px", textAlign: "center", color: "#1e293b", fontWeight: 600 }}>{nights}</td>
                            <td style={{ padding: "10px 14px", color: "#15803d", fontWeight: 600, textAlign: "right" }}>
                              {b.totalAmount != null ? `฿${fmtNum(b.totalAmount)}` : "-"}
                            </td>
                            <td style={{ padding: "10px 14px", color: b.paidAmount >= b.totalAmount ? "#15803d" : "#b45309", fontWeight: 600, textAlign: "right" }}>
                              {b.paidAmount != null ? `฿${fmtNum(b.paidAmount)}` : "-"}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

        </div>
      </div>
    </>
  );
}

const labelStyle = {
  display: "flex", flexDirection: "column", gap: 5,
  fontSize: 13, fontWeight: 600, color: "#475569",
};

const inputStyle = {
  padding: "8px 12px", borderRadius: 8, border: "1px solid #e2e8f0",
  fontSize: 14, color: "#1e293b", background: "#f8fafc",
  outline: "none", minWidth: 140,
};
